/**
 * URLAApplication — smart-subset URLA-style application form.
 *
 * Driven by src/config/mismoFormFields.js (MISMO 3.4 / Fannie 1003 schema). The
 * section-visibility resolver in src/utils/urlaSectionVisibility.js trims the
 * 17-section URLA down to only what's relevant for the borrower's stated
 * scenario (e.g. skip Real Estate Owned unless they own other property; skip
 * Co-Borrower unless explicitly added).
 *
 * Replaces the simplified 3-section MismoApplicationForm.jsx.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from '../../ui/components';
import { ArrowLeft, ArrowRight, Check, Plus, Trash2 } from 'lucide-react';
import {
  updateApplicationData,
  setCurrentStep,
  initializePrimaryBorrower,
  STEPS,
} from '../../store/loanApplicationSlice';
import {
  getVisibleUrlaSections,
  isFieldVisible,
} from '../../utils/urlaSectionVisibility';
import ApplyLayout from '../layout/ApplyLayout';
import { FormInput, CurrencyInput, RadioCardGroup } from '../forms';
import LiveReadinessScore from './LiveReadinessScore';
import ProposedDocuments from './ProposedDocuments';

const noop = () => {};

const SectionStepper = ({ sections, currentIndex, sectionData, onJump }) => (
  <div style={{
    display: 'flex', flexDirection: 'column', gap: 4,
    backgroundColor: 'var(--lk-bg-surface-1)',
    border: '1px solid var(--lk-border)',
    borderRadius: 'var(--lk-radius-lg)',
    padding: 12,
  }}>
    <div style={{
      fontSize: 'var(--lk-text-xs)', fontWeight: 600,
      color: 'var(--lk-text-tertiary)', textTransform: 'uppercase',
      letterSpacing: '0.6px', padding: '4px 8px 8px',
    }}>
      Application Sections
    </div>
    {sections.map((s, i) => {
      const filled = sectionData?.[s.key] && Object.keys(sectionData[s.key]).length > 0;
      const active = i === currentIndex;
      return (
        <button
          key={s.key}
          onClick={() => onJump(i)}
          style={{
            textAlign: 'left',
            padding: '8px 10px',
            borderRadius: 6,
            border: 'none',
            cursor: 'pointer',
            backgroundColor: active ? 'var(--lk-primary-50)' : 'transparent',
            color: active ? 'var(--lk-primary-700)' : 'var(--lk-text-primary)',
            display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 'var(--lk-text-sm)',
            fontWeight: active ? 600 : 500,
          }}
        >
          <span style={{
            width: 18, height: 18, borderRadius: '50%',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: filled ? 'var(--lk-success-500)' : 'var(--lk-gray-200)',
            color: filled ? 'white' : 'var(--lk-text-tertiary)',
            fontSize: 10, fontWeight: 700,
          }}>
            {filled ? <Check size={11} /> : i + 1}
          </span>
          <span style={{ flex: 1 }}>{s.title}</span>
        </button>
      );
    })}
  </div>
);

// ─────────────────────────────────────────────────────────────
// Field renderer
// ─────────────────────────────────────────────────────────────
const FieldRenderer = ({ field, value, onChange, error }) => {
  const common = {
    label: field.label,
    value: value ?? '',
    onChange,
    placeholder: field.placeholder,
    required: !!field.required,
    error,
  };

  switch (field.type) {
    case 'currency':
      return <CurrencyInput {...common} />;
    case 'number':
      return <FormInput {...common} type="number" />;
    case 'date':
      return <FormInput {...common} type="date" />;
    case 'email':
      return <FormInput {...common} type="email" />;
    case 'phone':
      return <FormInput {...common} type="tel" />;
    case 'ssn':
      return <FormInput {...common} type="password" placeholder={field.placeholder || 'XXX-XX-XXXX'} />;
    case 'select': {
      const options = (field.options || []).map((opt) => (
        typeof opt === 'string' ? { value: opt, label: opt } : opt
      ));
      // Use radio cards when options are short and limited; native select otherwise.
      if (options.length <= 5 && field.required && !field.multiselect) {
        return (
          <RadioCardGroup
            label={field.label}
            value={value ?? ''}
            onChange={onChange}
            error={error}
            required={!!field.required}
            options={options.map((o) => ({ value: o.value, label: o.label }))}
          />
        );
      }
      return (
        <div style={{ marginBottom: 24 }}>
          {field.label && (
            <label style={{
              fontSize: 14, fontWeight: 500,
              color: 'var(--lk-text-primary)', marginBottom: 8, display: 'block',
            }}>
              {field.label}
              {field.required && <span style={{ color: 'var(--lk-danger-500)', marginLeft: 4 }}>*</span>}
            </label>
          )}
          <select
            value={value ?? ''}
            onChange={onChange}
            style={{
              width: '100%', padding: '12px 16px', fontSize: 16,
              borderRadius: 'var(--lk-radius-md)',
              border: '1px solid var(--lk-border-default)',
              backgroundColor: 'white', outline: 'none',
            }}
          >
            <option value="">Select…</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      );
    }
    case 'multiselect': {
      const arr = Array.isArray(value) ? value : (value ? [value] : []);
      const toggle = (opt) => {
        const next = arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt];
        onChange({ target: { value: next } });
      };
      return (
        <div style={{ marginBottom: 24 }}>
          {field.label && (
            <label style={{
              fontSize: 14, fontWeight: 500,
              color: 'var(--lk-text-primary)', marginBottom: 8, display: 'block',
            }}>
              {field.label}
              {field.required && <span style={{ color: 'var(--lk-danger-500)', marginLeft: 4 }}>*</span>}
            </label>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {(field.options || []).map((opt) => {
              const selected = arr.includes(opt);
              return (
                <button
                  type="button"
                  key={opt}
                  onClick={() => toggle(opt)}
                  style={{
                    padding: '6px 12px', borderRadius: 999,
                    border: selected ? '1px solid var(--lk-primary-500)' : '1px solid var(--lk-border)',
                    backgroundColor: selected ? 'var(--lk-primary-50)' : 'var(--lk-bg-surface-1)',
                    color: selected ? 'var(--lk-primary-700)' : 'var(--lk-text-secondary)',
                    fontSize: 13, fontWeight: 500, cursor: 'pointer',
                  }}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    case 'boolean':
      return (
        <RadioCardGroup
          label={field.label}
          value={value === true || value === 'yes' ? 'yes' : value === false || value === 'no' ? 'no' : ''}
          onChange={(e) => onChange({ target: { value: e.target.value === 'yes' } })}
          required={!!field.required}
          error={error}
          options={[
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ]}
        />
      );
    default:
      return <FormInput {...common} />;
  }
};

// ─────────────────────────────────────────────────────────────
// Single-entry section editor
// ─────────────────────────────────────────────────────────────
const SingleEntrySection = ({ section, sectionData, allApplicationData, onChange }) => {
  const visibleFields = section.fields.filter((f) =>
    isFieldVisible(f, sectionData, allApplicationData)
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
      {visibleFields.map((field) => (
        <div
          key={field.id}
          style={{ gridColumn: field.fullWidth ? '1 / -1' : 'auto' }}
        >
          <FieldRenderer
            field={field}
            value={sectionData?.[field.id]}
            onChange={(e) => onChange(field.id, e?.target ? e.target.value : e)}
          />
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Multi-entry section editor (assets, liabilities, REO, etc.)
// ─────────────────────────────────────────────────────────────
const MultiEntrySection = ({ section, sectionData, allApplicationData, onChange }) => {
  const entries = sectionData?.entries || [];

  const updateEntries = (next) => onChange('entries', next);
  const addEntry = () => updateEntries([...entries, {}]);
  const removeEntry = (idx) => updateEntries(entries.filter((_, i) => i !== idx));
  const updateEntryField = (idx, fieldId, value) => {
    const next = entries.map((e, i) => (i === idx ? { ...e, [fieldId]: value } : e));
    updateEntries(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {entries.length === 0 && (
        <div style={{
          padding: 24, borderRadius: 'var(--lk-radius-lg)',
          border: '1px dashed var(--lk-border)',
          textAlign: 'center', color: 'var(--lk-text-tertiary)',
        }}>
          <p style={{ marginBottom: 12, fontSize: 'var(--lk-text-sm)' }}>
            No {section.entryLabel?.toLowerCase() || 'entries'} added yet. Add one only if it applies to you.
          </p>
          <Button variant="secondary" size="sm" icon={Plus} onClick={addEntry}>
            {section.addButtonLabel || 'Add'}
          </Button>
        </div>
      )}
      {entries.map((entry, idx) => {
        const visibleFields = section.fields.filter((f) =>
          isFieldVisible(f, entry, allApplicationData)
        );
        return (
          <div
            key={idx}
            style={{
              padding: 16, borderRadius: 'var(--lk-radius-lg)',
              border: '1px solid var(--lk-border)',
              backgroundColor: 'var(--lk-bg-surface-1)',
            }}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: 12,
            }}>
              <div style={{ fontWeight: 600, fontSize: 'var(--lk-text-sm)' }}>
                {section.entryLabel} #{idx + 1}
              </div>
              <button
                onClick={() => removeEntry(idx)}
                title={`Remove ${section.entryLabel}`}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--lk-danger-500)', display: 'flex', alignItems: 'center', gap: 4,
                  fontSize: 12, fontWeight: 500,
                }}
              >
                <Trash2 size={14} /> Remove
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
              {visibleFields.map((field) => (
                <div
                  key={field.id}
                  style={{ gridColumn: field.fullWidth ? '1 / -1' : 'auto' }}
                >
                  <FieldRenderer
                    field={field}
                    value={entry?.[field.id]}
                    onChange={(e) => updateEntryField(idx, field.id, e?.target ? e.target.value : e)}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {entries.length > 0 && (
        <Button variant="secondary" size="sm" icon={Plus} onClick={addEntry}>
          {section.addButtonLabel || 'Add another'}
        </Button>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────
const URLAApplication = () => {
  const dispatch = useDispatch();
  const productRecommendation = useSelector((s) => s.loanApplication.productRecommendation);
  const applicationData = useSelector((s) => s.loanApplication.applicationData);
  const recommendedProduct = useSelector((s) => s.loanApplication.recommendedProduct);

  const sections = useMemo(
    () => getVisibleUrlaSections(productRecommendation, applicationData),
    [productRecommendation, applicationData]
  );

  const [sectionIndex, setSectionIndex] = useState(0);
  const safeIndex = Math.min(sectionIndex, sections.length - 1);
  const currentSection = sections[safeIndex];

  // Auto-init the primary borrower row from assessment + auth user (one-shot).
  useEffect(() => {
    dispatch(initializePrimaryBorrower());
  }, [dispatch]);

  // Whenever the user changes a field, write straight into Redux.
  const handleSingleChange = (fieldId, value) => {
    dispatch(updateApplicationData({
      section: currentSection.key,
      data: { [fieldId]: value },
    }));
  };

  const handleMultiChange = (key, value) => {
    dispatch(updateApplicationData({
      section: currentSection.key,
      data: { [key]: value },
    }));
  };

  const handleNext = () => {
    if (safeIndex < sections.length - 1) {
      setSectionIndex(safeIndex + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      dispatch(setCurrentStep(STEPS.DOCUMENTS));
    }
  };

  const handleBack = () => {
    if (safeIndex > 0) {
      setSectionIndex(safeIndex - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      dispatch(setCurrentStep(STEPS.ASSESSMENT));
    }
  };

  if (!currentSection) {
    return (
      <ApplyLayout
        step={STEPS.APPLICATION + 1}
        totalSteps={5}
        title="Loading application…"
        subtitle={null}
        context="application"
      />
    );
  }

  const sectionData = applicationData?.[currentSection.key] || (currentSection.multiEntry ? { entries: [] } : {});
  const isOptional = currentSection.optional || currentSection.key === 'demographics';

  // Right-rail content: live readiness + dynamic doc list reacting to changes.
  const rightRail = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
      <LiveReadinessScore />
      <ProposedDocuments
        applicationData={applicationData}
        productId={recommendedProduct?.id}
        compact
      />
      <SectionStepper
        sections={sections}
        currentIndex={safeIndex}
        sectionData={applicationData}
        onJump={(i) => setSectionIndex(i)}
      />
    </div>
  );

  return (
    <ApplyLayout
      step={2}
      totalSteps={5}
      title={currentSection.title}
      subtitle={currentSection.description}
      context="application"
      rightRailContent={rightRail}
    >
      {isOptional && (
        <div style={{
          padding: 'var(--lk-space-3) var(--lk-space-4)',
          backgroundColor: 'var(--lk-info-50, var(--lk-primary-50))',
          borderRadius: 'var(--lk-radius-md)',
          marginBottom: 'var(--lk-space-4)',
          fontSize: 'var(--lk-text-sm)',
          color: 'var(--lk-text-secondary)',
        }}>
          This section is optional. You can skip it and come back later.
        </div>
      )}

      {currentSection.education && (
        <div style={{
          padding: 'var(--lk-space-3) var(--lk-space-4)',
          backgroundColor: 'var(--lk-bg-surface-2)',
          borderRadius: 'var(--lk-radius-md)',
          marginBottom: 'var(--lk-space-5)',
          fontSize: 'var(--lk-text-sm)',
          color: 'var(--lk-text-secondary)',
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{currentSection.education.title}</div>
          <div>{currentSection.education.description}</div>
        </div>
      )}

      {currentSection.multiEntry ? (
        <MultiEntrySection
          section={currentSection}
          sectionData={sectionData}
          allApplicationData={applicationData}
          onChange={handleMultiChange}
        />
      ) : (
        <SingleEntrySection
          section={currentSection}
          sectionData={sectionData}
          allApplicationData={applicationData}
          onChange={handleSingleChange}
        />
      )}

      <div style={{
        display: 'flex', gap: 'var(--lk-space-3)',
        marginTop: 'var(--lk-space-10)',
      }}>
        <Button variant="secondary" size="lg" icon={ArrowLeft} onClick={handleBack}>
          Back
        </Button>
        <Button variant="primary" size="lg" icon={ArrowRight} onClick={handleNext} style={{ flex: 1 }}>
          {safeIndex === sections.length - 1
            ? 'Continue to Documents'
            : `Next: ${sections[safeIndex + 1]?.title || 'Continue'}`}
        </Button>
      </div>

      <div style={{
        marginTop: 'var(--lk-space-6)', textAlign: 'center',
        color: 'var(--lk-text-quaternary)', fontSize: 'var(--lk-text-sm)',
      }}>
        Section {safeIndex + 1} of {sections.length}
        {recommendedProduct ? ` · Application for ${recommendedProduct.displayName || recommendedProduct.name}` : ''}
      </div>

      {/* Suppress unused-import lint */}
      {noop && null}
    </ApplyLayout>
  );
};

export default URLAApplication;
